import { enumLabel } from '../../utils/enumLabels';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Descriptions, Empty, Space, Spin, Tag, message } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import hepatoApi, { HepatoForm, HepatoSection } from '../../api/hepatoApi';
import { patientApi } from '../../api/patientApi';
import type { Patient } from '../../api/patientApi';
import {
  buildHepatoPdfFilename,
  downloadPdf,
  generateHepatoPdfFromTemplate,
} from '../../utils/hepatoPdfTemplate';

const sectionLabels: Array<[keyof HepatoForm, string]> = [
  ['generalData', 'I. Date generale'],
  ['currentSymptoms', 'II. Simptome actuale'],
  ['personalHistory', 'III. Istoric medical personal'],
  ['familyHistory', 'IV. Istoric medical familial'],
  ['riskFactors', 'V. Factori de risc si stil de viata'],
  ['previousExams', 'VI. Examinari anterioare'],
  ['initialDiagnosis', 'VII. Diagnostic initial'],
  ['diseaseStage', 'VIII. Stadiul bolii'],
  ['labsAndImaging', 'IX. Laborator si imagistica'],
  ['surgery', 'X. Chirurgie / interventii'],
  ['oncologicTreatment', 'XI. Tratament oncologic'],
  ['treatmentResponse', 'XII. Evaluarea raspunsului'],
  ['followUp', 'XIII. Evolutie si follow-up'],
  ['clinicalTrials', 'XIV. Studii clinice'],
  ['consent', 'XVI. Consimtamant'],
];

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'blue',
  REVIEWED: 'green',
  ARCHIVED: 'purple',
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Da' : 'Nu';
  }

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const downloadJson = (form: HepatoForm) => {
  const blob = new Blob([JSON.stringify(form, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `hepato-form-${form.id}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

const HepatoFormDetailPage = () => {
  const { id: patientId, hepatoId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<HepatoForm | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadForm = async () => {
      if (!hepatoId) {
        setLoading(false);
        return;
      }

      try {
        const [formData, patientData] = await Promise.all([
          hepatoApi.getById(hepatoId),
          patientId ? patientApi.getById(patientId) : Promise.resolve(null),
        ]);
        setForm(formData);
        setPatient(patientData);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Nu s-a putut incarca chestionarul hepatic');
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [hepatoId]);

  const handleExportPDF = async () => {
    if (!form || !patient) {
      message.error('Nu s-au putut genera datele pentru PDF');
      return;
    }

    try {
      message.loading({ content: 'Se genereaza PDF...', key: 'hepato-pdf-export' });
      const pdfBytes = await generateHepatoPdfFromTemplate(form, patient);
      downloadPdf(pdfBytes, buildHepatoPdfFilename(patient));
      message.success({ content: 'PDF generat cu succes!', key: 'hepato-pdf-export' });
    } catch (error) {
      console.error('Error generating hepato PDF:', error);
      message.error({ content: 'Eroare la generarea PDF-ului', key: 'hepato-pdf-export' });
    }
  };

  const printableSections = useMemo(() => {
    if (!form) {
      return [];
    }

    return sectionLabels
      .map(([key, label]) => ({ label, value: form[key] as HepatoSection | undefined }))
      .filter((section) => section.value && Object.keys(section.value).length > 0);
  }, [form]);

  if (loading) {
    return <Spin size="large" tip="Se incarca chestionarul hepatic..." />;
  }

  if (!form) {
    return <Empty description="Chestionarul hepatic nu a fost gasit" />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/patients/${patientId}`)}>
          Inapoi
        </Button>
        <Button icon={<DownloadOutlined />} onClick={() => downloadJson(form)}>
          Export JSON
        </Button>
        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>
          Export PDF
        </Button>
      </Space>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
        <h1>Chestionar Anamneza Tumori Hepatice</h1>
        <Tag color={statusColor[form.status]}>{enumLabel(form.status)}</Tag>
      </div>

      <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="ID pacient">{form.patientId}</Descriptions.Item>
        <Descriptions.Item label="Creat de">{form.createdBy}</Descriptions.Item>
        <Descriptions.Item label="Creat la">{dayjs(form.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
        <Descriptions.Item label="Actualizat la">{dayjs(form.updatedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
      </Descriptions>

      {printableSections.map((section) => (
        <div key={section.label} style={{ marginBottom: 24 }}>
          <h2>{section.label}</h2>
          <Descriptions bordered column={1} size="small">
            {Object.entries(section.value || {}).map(([key, value]) => (
              <Descriptions.Item key={key} label={key}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {formatValue(value)}
                </pre>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      ))}

      {form.notes && (
        <div style={{ marginBottom: 24 }}>
          <h2>XV. Observatii suplimentare</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{form.notes}</pre>
        </div>
      )}
    </div>
  );
};

export default HepatoFormDetailPage;
